import { FC, Fragment, useEffect } from "react";
import { observer } from "mobx-react";
import { SortedHeadCell } from "../../components-v2/SortedHeadCell";
import { totalNumberOfShares, totalNumberOfVotes } from "../../types/Shareholder";
import ShareholdersStore, { hasShares } from "../../stores/ShareholdersStore";
import { Table } from "../../components-v2/Table";
import { VBox } from "../../components-v2/Box";
import {
  useLanguage,
  useLocaleFormat,
  useRootStore,
  useTranslation,
  useUserStore,
} from "../../stores/RootStoreContext";
import { prepTableColumns } from "./shareholdersDomain";

const Shareholders: FC<{ controller: ShareholdersStore }> = observer(({ controller }) => {
  const { t } = useTranslation();
  const locale = useLanguage();
  const userStore = useUserStore();
  const { formatNumber, formatPercent } = useLocaleFormat();
  const { pathname: baseUrl, search: queryString } = useRootStore().location;
  const currentUrl = baseUrl + queryString;

  const {
    isLoading,
    shareSumBySharetypeCode,
    calculatedVotesBySharetypeCode,
    clearMessages,
    sortedBy,
    sortedByType,
    sortDirection,
    getTransactionLog,
  } = controller;

  const shareholders = controller.sortedShareholders;
  const filteredTypes = controller.filteredShareTypes;
  const hasSharesInShareholderStatus = shareholders.filter((sh) => hasShares(sh)).length > 0;
  useEffect(() => {
    clearMessages();
    getTransactionLog();
  }, [clearMessages, getTransactionLog]);

  if (isLoading) return null;

  const tableColumns = prepTableColumns(
    locale,
    controller.shareTypeFilter.value,
    shareholders,
    shareSumBySharetypeCode,
    controller.calculatedVotesBySharetypeCode,
    filteredTypes,
    t,
  );
  return (
    <VBox>
      <div data-testid="shareholderName">
        <Table hoverable>
          <thead>
            <tr>
              {tableColumns.map(({ name, shareTypeName, label, align, notSorted, width }) => {
                const active = name ? sortedBy === name : sortedByType === shareTypeName;
                return notSorted ? (
                  <td
                    key={`${name}-${shareTypeName}`}
                    align={align}
                    style={{ width, whiteSpace: "nowrap" }}
                  >
                    {t(label)}
                  </td>
                ) : (
                  <SortedHeadCell
                    key={`${name}-${shareTypeName}`}
                    label={t(label)}
                    align={align}
                    onClick={() =>
                      controller.setSorting(
                        name,
                        shareTypeName,
                        active && sortDirection === "asc" ? "desc" : "asc",
                      )
                    }
                    active={active}
                    direction={sortDirection}
                    style={{ width, whiteSpace: "nowrap" }}
                  />
                );
              })}
            </tr>
          </thead>
          <tbody>
            {shareholders.map((shareholder) => (
              <tr
                data-testid="shareholderName"
                key={`${shareholder.id}-${shareholder.name}`}
                onClick={() =>
                  userStore.navigateToShareholderDetail(shareholder.id, currentUrl, queryString)
                }
              >
                {tableColumns.map(({ name, shareTypeName, align, getValue }) => (
                  <td
                    key={`${name}-${shareTypeName}`}
                    align={align || "right"}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {getValue(shareholder)}
                  </td>
                ))}
              </tr>
            ))}
            {hasSharesInShareholderStatus && (
              <tr key="total">
                <td></td>
                <td colSpan={2} align="right" className={"tw-cursor-auto"}>
                  <b>{t("total")}</b>
                </td>
                {controller.isTotalOverview ? (
                  <Fragment key="totaloverview">
                    <td align="right" className={"tw-cursor-auto"}>
                      <b>{formatNumber(totalNumberOfShares(shareSumBySharetypeCode))}</b>
                    </td>
                    <td align="right" className={"tw-cursor-auto"}>
                      <b>{formatPercent(1)}</b>
                    </td>
                    <td align="right" className={"tw-cursor-auto"}>
                      <b>{formatNumber(totalNumberOfVotes(calculatedVotesBySharetypeCode))}</b>
                    </td>
                    <td align="right" className={"tw-cursor-auto"}>
                      <b>{formatPercent(1)}</b>
                    </td>
                  </Fragment>
                ) : controller.shareTypeFilter.value === "votes" ? (
                  <>
                    <td align="right" className={"tw-cursor-auto"}>
                      <b>
                        {formatNumber(
                          totalNumberOfVotes(controller.calculatedVotesBySharetypeCode),
                        )}
                      </b>
                    </td>
                    <td align="right" className={"tw-cursor-auto"}>
                      <b>{formatPercent(1)}</b>
                    </td>
                    {filteredTypes.map(({ sharetypeCode }) => (
                      <Fragment key={sharetypeCode}>
                        <td align="right" className={"tw-cursor-auto"}>
                          <b>
                            {formatNumber(
                              controller.calculatedVotesBySharetypeCode[sharetypeCode] ?? 0,
                            )}
                          </b>
                        </td>
                        <td align="right" className={"tw-cursor-auto"}>
                          <b>{formatPercent(1)}</b>
                        </td>
                      </Fragment>
                    ))}
                  </>
                ) : (
                  filteredTypes.map(({ sharetypeCode }) => (
                    <Fragment key={"filteredSharetypes-total" + sharetypeCode}>
                      <td align="right" key={"number" + sharetypeCode} className={"tw-cursor-auto"}>
                        <b>{formatNumber(shareSumBySharetypeCode[sharetypeCode] || 0)}</b>
                      </td>
                      <td
                        align="right"
                        key={"percent" + sharetypeCode}
                        className={"tw-cursor-auto"}
                      >
                        <b>{formatPercent(1)}</b>
                      </td>
                    </Fragment>
                  ))
                )}
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </VBox>
  );
});

export default Shareholders;
